import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// 🚀 Импортируем умную систему предзагрузки и мониторинг производительности
import smartPreloader from './utils/smartPreloader';
import performanceMonitor from './utils/performanceMonitor';

// Layout components
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';

// 🚀 ЛЕНИВАЯ ЗАГРУЗКА СТРАНИЦ ДЛЯ ОПТИМИЗАЦИИ
import { createLazyComponent } from './components/LazyLoader';

// Критические страницы (загружаем сразу)
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';

// Ленивая загрузка остальных страниц
const Training = createLazyComponent(() => import('./pages/Training'));
const TrainingDetail = createLazyComponent(() => import('./pages/TrainingDetail'));
const Shop = createLazyComponent(() => import('./pages/Shop'));
const About = createLazyComponent(() => import('./pages/About'));
const Education = createLazyComponent(() => import('./pages/Education'));
const Account = createLazyComponent(() => import('./pages/Account'));
const Admin = createLazyComponent(() => import('./pages/Admin'));
const Articles = createLazyComponent(() => import('./pages/Articles'));
const Article = createLazyComponent(() => import('./pages/Article'));
const PortfolioItem = createLazyComponent(() => import('./pages/PortfolioItem'));
const ReviewItem = createLazyComponent(() => import('./pages/ReviewItem'));
const Auth = createLazyComponent(() => import('./pages/Auth'));
const Login = createLazyComponent(() => import('./pages/Auth/Login'));
const Register = createLazyComponent(() => import('./pages/Auth/Register'));
const ForgotPassword = createLazyComponent(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = createLazyComponent(() => import('./pages/Auth/ResetPassword'));
const PaymentSuccess = createLazyComponent(() => import('./pages/PaymentSuccess'));
const PaymentCancel = createLazyComponent(() => import('./pages/PaymentCancel'));

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';

// Protected Route component
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';

// Other components
import CookieNotice from './components/CookieNotice';
import FlowerLoader from './components/UI/FlowerLoader';

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [globalApiLoading, setGlobalApiLoading] = useState(false);
  const [initialPageLoading, setInitialPageLoading] = useState(true);
  const isModalRoute =
    location.pathname === '/auth' ||
    location.pathname === '/account' ||
    location.pathname.startsWith('/profile/');
  const previousNonModalLocationRef = useRef(null);

  useEffect(() => {
    if (!isModalRoute) {
      previousNonModalLocationRef.current = location;
    }
  }, [location, isModalRoute]);

  useEffect(() => {
    const onGlobalLoading = (event) => {
      setGlobalApiLoading(Boolean(event?.detail?.loading));
    };

    window.addEventListener('ef-global-loading', onGlobalLoading);
    return () => {
      window.removeEventListener('ef-global-loading', onGlobalLoading);
    };
  }, []);

  useEffect(() => {
    const finishInitialLoading = () => setInitialPageLoading(false);

    if (document.readyState === 'complete') {
      finishInitialLoading();
      return undefined;
    }

    window.addEventListener('load', finishInitialLoading);
    return () => {
      window.removeEventListener('load', finishInitialLoading);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const backgroundLocation =
    location.state?.backgroundLocation ||
    (isModalRoute ? previousNonModalLocationRef.current : null);

  // 🚀 Инициализируем умную предзагрузку и мониторинг производительности
  useEffect(() => {
    smartPreloader.initialize();
    performanceMonitor.initialize();
    
    // Логируем финальный отчет через 10 секунд
    const reportTimeout = setTimeout(() => {
      console.log('🚀 ФИНАЛЬНЫЙ ОТЧЕТ ПРОИЗВОДИТЕЛЬНОСТИ:');
      performanceMonitor.logReport();
    }, 10000);
    
    return () => {
      clearTimeout(reportTimeout);
      performanceMonitor.cleanup();
    };
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <CurrencyProvider>
          <div className="min-h-screen bg-white text-gray-800">
          <Helmet>
            <html lang={i18n.language} />
            <title>Elena Fiore🌸</title>
            <meta name="description" content="Elena Fiore - бренд эксклюзивных авторских брошей цветов из шелка. Уникальность техники, авторский стиль, ручная работа, большая цветовая палитра и эксклюзивность" />
            <meta name="keywords" content="броши из шелка, цветы из шелка, авторские украшения, эксклюзивные броши, Elena Fiore, ручная работа, дизайнерские броши" />
            <meta property="og:title" content="Elena Fiore🌸 - Эксклюзивные броши из шелка" />
            <meta property="og:description" content="Бренд эксклюзивных авторских брошей цветов из шелка. Уникальность техники, авторский стиль, ручная работа" />
            <meta property="og:type" content="website" />
            <meta name="twitter:card" content="summary_large_image" />
          </Helmet>

          <Header />
          
          <main className="min-h-screen">
            <Routes location={backgroundLocation || location}>
              {/* Публичные маршруты */}
              <Route path="/" element={<Landing />} />
              <Route path="/training" element={<Training />} />
              <Route path="/training-detail" element={<TrainingDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/education" element={<Education />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:slug" element={<Article />} />
              <Route path="/portfolio/:id" element={<PortfolioItem />} />
              <Route path="/reviews/:id" element={<ReviewItem />} />
              
              {/* Платежи */}
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              
              {/* Авторизация */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/register" element={<Navigate to="/auth" replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              {/* <Route path="/verify-email/:token" element={<VerifyEmail />} /> */} {/* Временно отключено */}
              
              {/* Защищённые маршруты */}
              <Route path="/account" element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/*" element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              } />
              
              {/* Админские маршруты */}
              <Route path="/admin/*" element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              } />
              
              {/* Редиректы и 404 */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>

            {backgroundLocation && (
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/account" element={
                  <ProtectedRoute>
                    <Account />
                  </ProtectedRoute>
                } />
                <Route path="/profile/*" element={
                  <ProtectedRoute>
                    <Account />
                  </ProtectedRoute>
                } />
              </Routes>
            )}
          </main>
          
          <Footer />
          
          {/* Cookie Notice */}
          <CookieNotice />
          
          {/* Toast контейнер для уведомлений */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />

          {(initialPageLoading || globalApiLoading) && (
            <FlowerLoader fullScreen overlayClassName="z-[10000]" />
          )}
        </div>
        </CurrencyProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
